import React, { useState, useEffect } from 'react';
import { fixS3ImageUrl, DEFAULT_IMAGE_DATA_URI, isValidImageUrl } from './imageUtils';

/**
 * @param {Object} props - Component props
 * @param {string} props.image - Primary image URL
 * @param {string} props.coverimage - Fallback cover image URL
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class for the image
 * @param {Object} props.style - Additional inline styles
 * @param {string} props.loading - Loading strategy ("eager" or "lazy")
 * @returns {React.ReactElement} - Image component with consistent error handling
 */
const EventImage = ({ image, coverimage, alt, className, style, loading = "eager" }) => {
  // Caches processed URLs in component state (performance optimization)
  const [imageSrc, setImageSrc] = useState('');
  const [hasError, setHasError] = useState(false);
  
  // Prevents infinite error loops - only change source once
  const handleError = (e) => {
    if (!hasError) {
      setHasError(true);
      e.target.onerror = null; // Prevent infinite error loops
      
      // Primary image URL → Fallback image URL → Default SVG image
      if (e.target.src !== DEFAULT_IMAGE_DATA_URI) {
        e.target.src = DEFAULT_IMAGE_DATA_URI;
      }
    }
  };

  // Processes URLs once on component mount (performance optimization)
  useEffect(() => {
    // Process the image URL with fallback to coverimage
    const processedSrc = fixS3ImageUrl(image || coverimage);
    
    setImageSrc(processedSrc);
  }, [image, coverimage]); // Only re-process when image URLs change

  return (
    <img 
      src={imageSrc} 
      alt={alt || 'Event Image'}
      className={className || ''}
      style={style || {}}
      onError={handleError}
      loading={loading} // Uses loading="eager" for above-the-fold images (performance optimization)
    />
  );
};

export default EventImage;
